import { ObservableMap } from '@stencil/store';
import { ServiceType } from '@uipath/portal-shell-util';

import { Scope } from '../constants/constants';
import { isFalselyOrWhiteSpace } from '../store/common';
import { State } from '../store/types/state';
import { isMsiOrStandalone } from './EnvironmentUtil';
import Logger from './logger/logger';
import { getScopedUrl } from './menu';

export function parseArrayProps(prop: string): string[] {
    return prop?.split?.(',').map(s => s.toLowerCase().trim()) ?? [];
}

export function caseInsensitiveCompare(str1: string, str2: string) {
    return str1.toLowerCase() === str2.toLowerCase();
}

export function isDefined<T>(value: T | null | undefined): value is T {
    return typeof value !== 'undefined' && value != null;
}

export const isString = (value: string | null | undefined): value is string => {
    return typeof value === 'string';
};

export const isNonEmptyString = (value: string | null | undefined): value is string => {
    return isString(value) && value.length > 0;
};

export const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (_, value) => {
        if (typeof value === 'object' && value != null) {
            if (seen.has(value)) {
                return;
            }
            seen.add(value);
        }
        return value;
    };
};

export const getScopedUrlFromStore = (serviceType: ServiceType, scope: Scope, store: ObservableMap<State>) => {
    const accountName = store.get('accountName');
    const isStandAlone = !!isMsiOrStandalone(store);
    const baseUrl = store.get('baseUrl');
    const tenantName = store.get('currentTenant');
    const organizationStandardUrl = store.get('organizationStandardUrl');
    const standardUrl = isStandAlone ? baseUrl : organizationStandardUrl || `${baseUrl}/${accountName}`;
    return getScopedUrl(
        standardUrl,
        tenantName,
        serviceType,
        scope,
        isStandAlone);
};

/**
 * `parseOrReturn(item, fallback)` helps in the case where `item` could be a valid object _or_ a string that should be parsed.
 * This is a common case with the Stencil property API.
 *
 * - If `item` is not a string, `item` will be returned
 * - If `item` is a string, and it is successfully parsed, the parsed object will be returned
 * - If `item` is a string, and parse fails, the `fallback` item will be returned.
 */
export const parseOrReturn = <T>(item: T | string, fallback: T) => {
    if (typeof item === 'string') {
        try {
            return JSON.parse(item);
        } catch (err) {
            Logger.error('utils', `Error parsing ${item}: ${err}`);
            return fallback;
        }
    }

    return item;
};

/**
 * Helper function to sanitize a token for logs
 * @returns "<empty>" if the token is null or empty, "<token>" otherwise
 */
export const sanitizeToken = (token?: string | null): string => {
    return isFalselyOrWhiteSpace(token) ? '<empty>' : '<token>';
};

export const sanitizePii = (pii?: string | null): string => {
    return isFalselyOrWhiteSpace(pii) ? '<empty>' : '<pii>';
};

export const removeObjectUndefinedProperties = (obj: any) => {
    const tempObj = { ...obj };
    Object.entries(tempObj).forEach(([ key, value ]) => {
        if (value == null) {
            delete tempObj[key];
        }
    });
    return tempObj;
};
